/**
 * เรื่องที่ต้องตรวจ — ปลายทางของการ์ด "เรื่องเร่งด่วนรอตรวจ" บนแดชบอร์ดเจ้าของ/HR
 *
 * ก่อนหน้านี้การ์ดกดไม่ได้ · รายการมีแค่ท้ายแท็บสรุปเวลา (ตัดที่ 100 แถว ขณะที่การ์ดนับ 975)
 * และไม่มีปุ่ม "แก้แล้ว/ข้าม" บนจอเลย ทั้งที่ API มีอยู่ → รายการกองขึ้นเรื่อย ๆ
 *
 * กรอง: ประเภท (ชิปพร้อมจำนวน) · เดือน · ชื่อ/รหัส · เลือกหลายรายการแล้วกดแก้แล้ว/ข้าม (ข้ามต้องมีเหตุผล)
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Search, SkipForward } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';
import { useEscalationBoard, useResolveEscalations } from '../../hooks';
import { escTypeLabel } from './Reconcile';

const PAGE_SIZE = 100;

export function OwnerEscalations() {
  const [type, setType] = useState<string | null>(null);
  const [period, setPeriod] = useState<string>('');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const toast = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // เปลี่ยนตัวกรอง = กลับหน้าแรก + ล้างที่เลือก (กันกดข้ามรายการที่มองไม่เห็นแล้ว)
  useEffect(() => {
    setOffset(0);
    setSelected(new Set());
  }, [type, period, debouncedQ]);

  const board = useEscalationBoard({ type, period: period || null, q: debouncedQ, offset, limit: PAGE_SIZE });
  const resolve = useResolveEscalations();
  const rows = board.data?.rows ?? [];
  const total = board.data?.total ?? 0;
  const byType = board.data?.by_type ?? {};
  const allTypesTotal = useMemo(() => Object.values(byType).reduce((a, b) => a + b, 0), [byType]);
  const typeChips = useMemo(
    () => Object.entries(byType).sort((a, b) => b[1] - a[1]),
    [byType],
  );

  const pageIds = rows.map((r) => r.esc_id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function togglePage() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  }

  async function act(resolution: 'resolved' | 'ignored') {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (resolution === 'ignored' && note.trim().length < 3) {
      toast.error('กรุณาระบุเหตุผลที่ข้าม');
      return;
    }
    const verb = resolution === 'resolved' ? 'ทำเครื่องหมายว่าแก้แล้ว' : 'ข้าม';
    const ok = await confirm({
      title: `${verb} ${ids.length} รายการ?`,
      message: resolution === 'ignored' ? `เหตุผล: ${note.trim()}` : 'รายการจะหายจากคิวและการ์ดบนแดชบอร์ด (ประวัติยังเก็บไว้)',
      confirmText: verb,
      danger: resolution === 'ignored',
    });
    if (!ok) return;
    try {
      const res = await resolve.mutateAsync({ esc_ids: ids, resolution, note: note.trim() || undefined });
      setSelected(new Set());
      setNote('');
      if (res.skipped > 0) {
        toast.success(`${verb}แล้ว ${res.updated} รายการ · ข้ามไป ${res.skipped} รายการเพราะมีคนตรวจไปก่อนแล้ว`);
      } else {
        toast.success(`${verb}แล้ว ${res.updated} รายการ`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="เรื่องที่ต้องตรวจ" subtitle="รายการผิดปกติจากการสรุปเวลาและการสแกน ที่ยังไม่มีใครตรวจ">
      {/* ตัวกรอง */}
      <div className="card p-3 mb-3 space-y-3">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setType(null)}
            className={`px-3 py-1.5 rounded-full text-sm border whitespace-nowrap ${type === null ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-700 border-gray-300'}`}
          >
            ทั้งหมด {allTypesTotal.toLocaleString('th-TH')}
          </button>
          {typeChips.map(([t, n]) => (
            <button
              key={t}
              type="button"
              onClick={() => setType(t)}
              className={`px-3 py-1.5 rounded-full text-sm border whitespace-nowrap ${type === t ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-700 border-gray-300'}`}
            >
              {escTypeLabel(t)} {n.toLocaleString('th-TH')}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="block min-w-0">
            <span className="text-xs text-gray-500">เดือน</span>
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="input w-full"
            />
          </label>
          <label className="block min-w-0">
            <span className="text-xs text-gray-500">ค้นหาชื่อหรือรหัสพนักงาน</span>
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="เช่น สมชาย หรือ EMP001"
                className="input w-full pl-9"
              />
            </div>
          </label>
        </div>
        {period && (
          <button type="button" className="text-xs text-brand-700 underline" onClick={() => setPeriod('')}>
            ล้างตัวกรองเดือน
          </button>
        )}
      </div>

      {/* รายการ */}
      {board.isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => <div key={i} className="card p-3 h-16 animate-pulse bg-gray-50" />)}
        </div>
      ) : board.isError ? (
        <ErrorState onRetry={() => board.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={CheckCircle2}
          title={allTypesTotal === 0 && !period && !debouncedQ ? 'ไม่มีเรื่องที่ต้องตรวจ' : 'ไม่พบรายการตามตัวกรองนี้'}
        />
      ) : (
        <>
          <div className="flex items-center justify-between mb-2 gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 min-h-[44px]">
              <input type="checkbox" className="w-5 h-5" checked={allOnPageSelected} onChange={togglePage} />
              เลือกทั้งหน้านี้ ({pageIds.length})
            </label>
            <p className="text-xs text-gray-500 whitespace-nowrap">
              {offset + 1}–{offset + rows.length} จาก {total.toLocaleString('th-TH')}
            </p>
          </div>
          <div className="space-y-2">
            {rows.map((r) => (
              <label key={r.esc_id} className={`card p-3 flex gap-3 items-start cursor-pointer ${selected.has(r.esc_id) ? 'ring-2 ring-brand-500' : ''}`}>
                <input
                  type="checkbox"
                  className="w-5 h-5 mt-0.5 shrink-0"
                  checked={selected.has(r.esc_id)}
                  onChange={() => toggle(r.esc_id)}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex justify-between gap-2">
                    <p className="font-medium text-sm truncate">{r.emp_name}</p>
                    <span className="badge-warn whitespace-nowrap inline-flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {escTypeLabel(r.type)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600">{r.date} · {r.emp_code}</p>
                  {r.detail && <p className="text-xs text-gray-500 mt-1 break-words">{r.detail}</p>}
                </div>
              </label>
            ))}
          </div>
          <div className="flex justify-between mt-3 gap-2">
            <button
              type="button"
              className="btn-secondary"
              disabled={offset === 0}
              onClick={() => { setOffset(Math.max(0, offset - PAGE_SIZE)); setSelected(new Set()); }}
            >
              หน้าก่อน
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={offset + rows.length >= total}
              onClick={() => { setOffset(offset + PAGE_SIZE); setSelected(new Set()); }}
            >
              หน้าถัดไป
            </button>
          </div>
        </>
      )}

      {/* แถบคำสั่ง — โผล่เมื่อเลือกอย่างน้อย 1 รายการ */}
      {selected.size > 0 && (
        <div className="sticky bottom-0 mt-4 -mx-4 px-4 py-3 bg-white border-t shadow-lg space-y-2">
          <p className="text-sm font-medium">เลือกแล้ว {selected.size} รายการ</p>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="เหตุผล (บังคับเมื่อกดข้าม) เช่น ระบบยังไม่เปิดใช้จริงเดือนนี้"
            className="input w-full"
          />
          <div className="flex gap-2 justify-end">
            <button type="button" className="btn-secondary inline-flex items-center gap-1" disabled={resolve.isPending} onClick={() => act('ignored')}>
              <SkipForward className="w-4 h-4" /> ข้าม
            </button>
            <button type="button" className="btn-primary inline-flex items-center gap-1" disabled={resolve.isPending} onClick={() => act('resolved')}>
              <CheckCircle2 className="w-4 h-4" /> แก้แล้ว
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
